```vue
<template>
<div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">

  <!-- Sidebar -->
  <HrSidebar :isOpen="isSidebarOpen" />

  <!-- Main -->
  <main
    class="flex-1 overflow-x-hidden transition-all duration-300"
    :class="{ 'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen }"
  >

    <!-- Navbar -->
    <header class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm">
      <HrTopbar
        :page-title="currentPageTitle"
        :is-sidebar-open="isSidebarOpen"
        :is-dark-mode="isDarkMode"
        @toggle-sidebar="toggleSidebar"
        @toggle-dark-mode="toggleDarkMode"
      />
    </header>

    <!-- Content -->
    <div class="p-6 mt-16">

      <!-- Top Bar -->
      <div class="flex justify-between items-center mb-8 p-4">

        <input
          v-model="search"
          type="text"
          placeholder="Search leave type..."
          class="border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 w-72 focus:outline-none focus:ring focus:ring-secondary dark:bg-gray-700 dark:text-white"
        />

        <button
          @click="openCreate"
          class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f] shadow"
        >
          + Add Leave Type
        </button>

      </div>

      <!-- Leave Grid -->
      <div
        v-if="filteredLeaves.length"
        class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6"
      >

        <div
          v-for="leave in filteredLeaves"
          :key="leave.id"
          class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-xl transition"
        >

          <!-- Header -->
          <div class="flex items-center space-x-3 mb-4">

            <div
              class="w-12 h-12 rounded-full bg-secondary text-white flex items-center justify-center font-bold"
            >
              {{ leave.code }}
            </div>

            <div>
              <h3 class="font-semibold text-gray-800 dark:text-white">
                {{ leave.name }}
              </h3>
            </div>

          </div>

          <!-- Description -->
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-6">
            {{ leave.description }}
          </p>

          <!-- Actions -->
          <div class="flex justify-between text-sm">

            <button
              @click="editLeave(leave)"
              class="text-blue-600 hover:underline"
            >
              Edit
            </button>

            <button
              @click="deleteLeave(leave.id)"
              class="text-red-500 hover:underline"
            >
              Delete
            </button>

          </div>

        </div>

      </div>

      <!-- Empty State -->
      <div
        v-else
        class="text-center text-gray-400 mt-20"
      >
        No leave types created yet
      </div>


      <!-- Modal -->
      <Transition name="modal">
        <div
          v-if="showModal"
          class="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
        >

          <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px] shadow-lg">

            <h3 class="text-lg font-semibold text-[#035a76] mb-6">
              {{ editing ? 'Edit Leave Type' : 'Create Leave Type' }}
            </h3>

            <form @submit.prevent="saveLeave" class="space-y-4">

              <input
                v-model="form.name"
                type="text"
                placeholder="Leave Type Name"
                required
                class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
              />

              <input
                v-model="form.code"
                type="text"
                placeholder="Code (AL, SL, ML...)"
                required
                class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
              />

              <textarea
                v-model="form.description"
                placeholder="Description"
                class="w-full border border-gray-300 dark:border-gray-600 rounded p-2 dark:bg-gray-700 dark:text-white"
              ></textarea>

              <div class="flex justify-end space-x-3 pt-2">

                <button
                  type="button"
                  @click="closeModal"
                  class="px-4 py-2 bg-gray-300 rounded"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]"
                >
                  Save
                </button>

              </div>

            </form>

          </div>

        </div>
      </Transition>

    </div>

  </main>
</div>
</template>


<script setup>

import { ref, computed, onMounted } from "vue"

import HrSidebar from "../../components/hr/HrSidebar.vue"
import HrTopbar from "../../components/hr/HrTopbar.vue"

import api from "@/services/api"


const currentPageTitle = ref("Leave Types")

const isSidebarOpen = ref(true)
const isDarkMode = ref(false)

const showModal = ref(false)
const editing = ref(false)

const search = ref("")

const leaves = ref([])


// FORM

const form = ref({
  id: null,
  name: "",
  code: "",
  description: ""
})


// GET LEAVE TYPES

const getLeaveTypes = async () => {

  try {

    const response = await api.get("/LeaveTypes.php")

    leaves.value = response.data

  } catch (error) {

    console.error("Error loading leave types:", error)

  }

}


// SEARCH

const filteredLeaves = computed(() => {

  return leaves.value.filter(l =>
    l.name.toLowerCase().includes(search.value.toLowerCase())
  )

})


// OPEN CREATE

const openCreate = () => {

  editing.value = false

  form.value = {
    id: null,
    name: "",
    code: "",
    description: ""
  }

  showModal.value = true

}


// EDIT

const editLeave = (leave) => {

  editing.value = true

  form.value = {
    id: leave.id,
    name: leave.name,
    code: leave.code,
    description: leave.description
  }

  showModal.value = true

}


// SAVE

const saveLeave = async () => {

  try {

    if (editing.value) {

      // UPDATE

      const response = await api.put("/LeaveTypes.php", {
        id: form.value.id,
        name: form.value.name,
        code: form.value.code,
        description: form.value.description
      })

      if (response.data.status === "error") {

        alert(response.data.message)

        return

      }

    } else {

      // CREATE

      const response = await api.post("/LeaveTypes.php", {
        name: form.value.name,
        code: form.value.code,
        description: form.value.description
      })

      if (response.data.status === "error") {

        alert(response.data.message)

        return

      }

    }

    await getLeaveTypes()

    closeModal()

  } catch (error) {

    console.error("Error saving leave type:", error)

  }

}


// DELETE

const deleteLeave = async (id) => {

  if (!confirm("Are you sure you want to delete this leave type?")) {
    return
  }

  try {

    const response = await api.delete("/LeaveTypes.php", {
      data: {
        id: id
      }
    })

    if (response.data.status === "error") {

      alert(response.data.message)

      return

    }

    await getLeaveTypes()

  } catch (error) {

    console.error("Error deleting leave type:", error)

  }

}


// CLOSE MODAL

const closeModal = () => {

  showModal.value = false

}


// SIDEBAR

const toggleSidebar = () => {

  isSidebarOpen.value = !isSidebarOpen.value

}


// DARK MODE

const toggleDarkMode = () => {

  isDarkMode.value = !isDarkMode.value

  if (isDarkMode.value) {

    document.documentElement.classList.add("dark")

  } else {

    document.documentElement.classList.remove("dark")

  }

}


// LOAD DATA WHEN PAGE OPENS

onMounted(() => {

  getLeaveTypes()

})

</script>
```



<style>

.modal-enter-active,
.modal-leave-active {
  transition: all .25s ease;
}

.modal-enter-from {
  opacity: 0;
  transform: scale(.9);
}

.modal-leave-to {
  opacity: 0;
  transform: scale(.9);
}

</style>

